(() => {
  const states = new WeakMap();
  class SubscriptionSelector extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const initialize = () => {
        if (!this.isConnected || this.initialized) return;
        const data = this.querySelector('[data-subscription-config]');
        const form = this.closest('form');
        if (!data || !form) return;
        try { this.config = JSON.parse(data.textContent); } catch { return; }
        this.form = form;
        this.input = this.querySelector('[name="selling_plan"]');
        this.labels = Object.fromEntries(Object.entries(this.config.labels).map(([key, value]) => [key, this.decode(value)]));
        this.config.oneTimeButton = this.decode(this.config.oneTimeButton || '');
        this.config.subscribeButton = this.decode(this.config.subscribeButton || '');
        const previous = states.get(form);
        this.selection = previous?.productId === this.config.productId ? {...previous} : {
          productId: this.config.productId,
          mode: this.config.initialPlan === '' ? 'onetime' : 'subscribe',
          planId: this.config.initialPlan || ''
        };
        this.initialized = true;
        const options = {signal: this.abort.signal};
        this.addEventListener('click', event => {
          const control = event.target.closest('button[data-mode],button[data-plan]');
          if (!control || !this.contains(control)) return;
          if (control.dataset.mode) this.selection.mode = control.dataset.mode;
          else { this.selection.mode = 'subscribe'; this.selection.planId = control.dataset.plan; }
          this.sync();
        }, options);
        form.addEventListener('variant:change', () => this.sync(), options);
        form.addEventListener('change', event => {
          if (event.target.name === 'id') this.sync();
        }, options);
        form.addEventListener('submit', event => {
          this.sync();
          if (!this.valid()) { event.preventDefault(); event.stopImmediatePropagation(); }
        }, {...options, capture: true});
        this.sync();
        window.Shopify?.PaymentButton?.init?.();
      };
      queueMicrotask(initialize);
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.initialized = false;
    }
    valid() {
      return !!this.variant && (this.selection.mode !== 'subscribe' || this.variant.plans.some(plan => String(plan.id) === this.input.value));
    }
    decode(text) {
      const decoder = document.createElement('textarea');
      decoder.innerHTML = String(text);
      return decoder.value;
    }
    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {style: 'currency', currency: this.config.currency || 'GBP'}).format(cents / 100);
    }
    label(key, values = {}) {
      return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`__${name}__`, String(value)), this.labels[key] || '');
    }
    setText(role, text) {
      const element = this.querySelector(`[data-subscription-${role}]`);
      if (element) element.textContent = text;
    }
    sync() {
      if (!this.initialized) return;
      const id = this.config.context === 'cart' ? this.config.variantId : this.form.querySelector('[name="id"]')?.value || this.config.variantId;
      this.variant = this.config.variants.find(variant => String(variant.id) === String(id));
      const plans = this.variant?.plans || [];
      let plan = plans.find(candidate => String(candidate.id) === String(this.selection.planId));
      if (!plan) plan = plans[0];
      this.selection.planId = plan ? String(plan.id) : '';
      if (this.variant?.requiresPlan) this.selection.mode = 'subscribe';
      else if (!plans.length) this.selection.mode = 'onetime';
      const subscribed = this.selection.mode === 'subscribe' && !!plan;
      const value = subscribed ? String(plan.id) : '';
      const changed = this.input.value !== value;
      this.input.value = value;
      this.input.setAttribute('value', value);
      this.input.disabled = !subscribed;
      states.set(this.form, {...this.selection});
      this.querySelectorAll('[data-mode-block]').forEach(block => {
        const active = block.dataset.modeBlock === this.selection.mode;
        block.classList.toggle('active', active);
        block.querySelector('.purchase-body').classList.toggle('open', active);
        block.querySelector('.purchase-body').hidden = !active;
        const button = block.querySelector('button[data-mode]');
        button.setAttribute('aria-pressed', String(active));
        button.disabled = button.dataset.mode === 'subscribe' ? !plans.length : !!this.variant?.requiresPlan;
        block.hidden = button.dataset.mode === 'onetime' && !!this.variant?.requiresPlan;
      });
      const frequencies = this.querySelector('[data-subscription-frequencies]');
      const signature = plans.map(item => `${item.id}:${item.interval}`).join('|');
      if (frequencies.dataset.signature !== signature) {
        frequencies.replaceChildren(...plans.map(item => {
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'frequency-option';
          button.dataset.plan = String(item.id); button.textContent = item.interval;
          return button;
        }));
        frequencies.dataset.signature = signature;
      }
      frequencies.querySelectorAll('button').forEach(button => {
        const active = button.dataset.plan === this.selection.planId;
        button.classList.toggle('active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      const price = this.variant?.price || 0;
      this.setText('onetime-price', this.money(price));
      this.setText('onetime-points', this.label('onePoints', {points: Math.floor(price / 100 * this.config.pointsPerPound)}));
      if (plan) {
        const savings = Math.max(0, price - plan.price);
        this.setText('discount', savings ? this.label('save', {amount: plan.adjustmentType === 'percentage' ? `${plan.adjustmentValue}%` : this.money(savings)}) : '');
        this.setText('strike', this.money(price));
        this.querySelector('[data-subscription-strike]').hidden = !savings;
        this.setText('price', this.money(plan.price));
        const base = Math.floor(plan.price / 100 * this.config.pointsPerPound);
        this.setText('points', this.label('subPoints', {first: base + this.config.bonusPoints, points: base, interval: plan.interval}));
      }
      Array.from(this.form.elements).filter(button => button.matches('.add-to-cart-button,.sticky-atc-button')).forEach(button => {
        const content = button.querySelector('button-content');
        const customLabels = button.closest('product-rerender')?.querySelector('[data-subscription-button-labels]');
        const text = subscribed ? customLabels?.dataset.subscription || this.config.subscribeButton : customLabels?.dataset.onetime || this.config.oneTimeButton;
        if (content && !button.disabled && text) {
          content.childNodes.forEach(node => { if (node.nodeType === Node.TEXT_NODE) node.textContent = text; });
          for (const name of ['rotated-text','up-text']) if (content.hasAttribute(name)) content.setAttribute(name, text);
        }
      });
      if (changed || !this.notified) {
        this.notified = true;
        this.input.dispatchEvent(new Event('input', {bubbles: true}));
        this.input.dispatchEvent(new Event('change', {bubbles: true}));
        this.form.dispatchEvent(new CustomEvent('subscription:mode-changed', {bubbles: true, detail: {mode: this.selection.mode, sellingPlanId: value, formId: this.form.id}}));
      }
    }
  }
  if (!customElements.get('sa-subscription-selector')) customElements.define('sa-subscription-selector', SubscriptionSelector);
})();
